import React, {
  Dispatch, ReactElement, SetStateAction, useState,
} from 'react';
import {
  View, Text, StyleSheet, ViewStyle, Pressable,
} from 'react-native';
import Colors from '../../../../constants/Colors';
import InfoIcon16 from '../../../16pxIcons/InfoIcon16';
import DateLabel from '../../DateLabel';
import DaySelector from '../../DaySelector';
import OccupancyIcon16 from '../../../16pxIcons/OccupancyIcon16';
import Style from '../../../../constants/Style';
import Button from '../../../Button';
import titleCase from '../../../TitleCase';
import { PressableState } from '../../../PressableStyled';
import asNumber from '../../../AsNumber';

interface SpaceUtilizationWidgetProps {
  icon?: ReactElement,
  title?: string,
  content: ReactElement | ReactElement[],
  style?: ViewStyle | ViewStyle[],
  contentStyle?: ViewStyle | ViewStyle[],
  row?: boolean,
  showDayDropdown?: boolean,
  selectedDayIndex?: number,
  setSelectedDayIndex?: Dispatch<SetStateAction<number>>,
  label?: string,
  count?: number,
  countCompared?: number,
  unit?: string,
  plural?: boolean,
  contentTitles?: [string, string],
  tooltipText?: string,
  alternateLabel?: string,
  alternateCount?: number,
}

const SpaceUtilizationWidget = ({
  icon = <OccupancyIcon16 />, title = 'Occupancy', content, style, row, showDayDropdown, label,
  unit, contentStyle, selectedDayIndex, setSelectedDayIndex, plural, contentTitles, tooltipText,
  count, countCompared, alternateLabel, alternateCount,
}: SpaceUtilizationWidgetProps) => {
  const [contentShownIndex, setContentShownIndex] = useState<number>(0);
  const [iconHovered, setIconHovered] = useState<boolean>(false);
  const [tooltipHovered, setTooltipHovered] = useState<boolean>(false);
  const [dayDropdownOpen, setDayDropdownOpen] = useState<boolean>(false);
  const tooltipShown: boolean = iconHovered || tooltipHovered;

  return <View style={[styles.container, style, row && styles.rowContainer]}>
    <View style={{ zIndex: (row || dayDropdownOpen) ? 5 : 3 }}>
      <View style={styles.contentHeader}>
        <View style={styles.contentSubHeader}>
          <View style={styles.icon}>
            {icon}
          </View>
          <Text style={styles.title}>{title}</Text>
          {tooltipText?.length > 0 && <View>
            <Pressable
              onHoverIn={() => setIconHovered(true)}
              onHoverOut={() => setIconHovered(false)}
              style={({ hovered }: PressableState) => [hovered && Style.hovered]}
            >
              <InfoIcon16 />
            </Pressable>
            {tooltipShown && <View
              style={styles.tooltipContainer}
              onPointerEnter={() => setTooltipHovered(true)}
              onPointerLeave={() => setTooltipHovered(false)}
            >
              <View style={styles.tooltip}>
                <Text
                  style={styles.tooltipText}
                  selectable
                >
                  {tooltipText}
                </Text>
              </View>
            </View>}
          </View>}
        </View>
        {showDayDropdown
          ? <DaySelector
            {...{
              selectedDayIndex, setSelectedDayIndex, plural, dayDropdownOpen, setDayDropdownOpen,
            }}
          /> : null}
        {!showDayDropdown && contentTitles?.length > 0 && <View style={Style.row}>
          {contentTitles.map((contentTitle: string, index: number) => <Button
            affirmative={contentShownIndex === index}
            onPress={() => setContentShownIndex(index)}
            title={contentTitle}
            style={styles.tabButton}
            textStyle={styles.tabButtonText}
          />)}
        </View>}
      </View>
      <DateLabel />
    </View>
    <View style={Style.row}>
      {alternateLabel?.length > 0 && <View style={{ flex: 1 }}>
        <Text style={styles.label}>{alternateLabel}</Text>
        <View style={styles.countContainer}>
          <Text style={styles.count}>{alternateCount ?? '-'}</Text>
          {unit?.length > 0 && <Text style={styles.unit}>{unit}</Text>}
        </View>
      </View>}
      <View style={{ flex: 1 }}>
        {label?.length > 0 && <Text style={styles.label}>{label}</Text>}
        <View style={styles.countContainer}>
          {label?.length > 0 && <Text style={styles.count}>{count ?? '-'}</Text>}
          {unit?.length > 0 && <Text style={styles.unit}>{unit}</Text>}
          {typeof asNumber(countCompared) === 'number' && <>
            <Text style={[styles.unit, { marginHorizontal: 24 }]}>{'vs'}</Text>
            <Text style={[styles.count, styles.countCompared]}>{asNumber(countCompared)}</Text>
            {unit?.length > 0 && <Text style={styles.unit}>{unit}</Text>}
          </>}
        </View>
      </View>
    </View>
    <View style={[row ? styles.rowContent : styles.content, contentStyle]}>
      {contentShownIndex === 0 ? content[0] ?? content : content[contentShownIndex]
        ?? <View style={styles.noContent}>
          <Text style={styles.label}>
            {'Content unavailable for '}
            {titleCase(contentTitles[contentShownIndex]?.toLowerCase())}
          </Text>
        </View>}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#FFF',
  },
  rowContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  contentHeader: {
    ...Style.row,
    marginBottom: 4,
    zIndex: 2,
  },
  contentSubHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksWhite200,
    justifyContent: 'center',
    alignItems: 'center',
    width: 24,
    height: 24,
  },
  title: {
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    marginHorizontal: 8,
  },
  item: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite200,
    marginTop: 8,
    borderRadius: 12,
  },
  content: {
    marginTop: 24,
    zIndex: 4,
  },
  rowContent: {
    marginStart: 24,
    flex: 1,
    zIndex: 4,
  },
  label: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginTop: 24,
    marginBottom: 4,
  },
  countContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  count: {
    fontSize: 30,
    lineHeight: 30,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginEnd: 4,
  },
  countCompared: {
    color: Colors.light.hereworksYellow600,
  },
  unit: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  tabButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginStart: 8,
    height: 28,
  },
  tabButtonText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
  },
  noContent: {
    height: 320,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tooltipContainer: {
    position: 'absolute',
    paddingStart: 20,
  },
  tooltip: {
    backgroundColor: '#FFF',
    width: 180,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_500Medium',
    letterSpacing: 0.15,
    color: Colors.light.hereworksBlue400,
  },
});

export default SpaceUtilizationWidget;
